import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { JiraProductDiscoveryLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";
import { SuccessProgressBar } from "@atlaskit/progress-bar";

import { wacToken } from "@/libraries/design/foundations";
import { useIsMobile } from "@/utils/useIsMobile.ts";

import { JiraProductDiscoveryLoadingProductComponent } from "./jira-product-discovery-loading-product-component.tsx";

const backdropContainer = css({
    display: "flex",
    position: "fixed",
    justifyContent: "center",
    alignItems: "center",
    insetBlockStart: "0px",
    insetInlineEnd: "0px",
    insetInlineStart: "0px",
    insetBlockEnd: "0px"
});

const modalContainer = css({
    backgroundColor: wacToken("color.neutral.light0"),
    boxShadow:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "0 8px 12px rgba(9, 30, 66, 0.15), 0 0 1px rgba(9, 30, 66, 0.31)",
    borderRadius: "4px",
    overflow: "visible",
    display: "flex",
    justifyContent: "left",
    alignItems: "center",
    margin: "auto 32px",
    padding: 24,
    minHeight: 665,
    width: 1130,
    maxWidth: "100%",
    [media.below.sm]: {
        margin: "auto 8px"
    }
});

const progressContainer = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%"
});

const logoContainer = css({
    "marginBlockEnd": 32,
    // eslint-disable-next-line
    " svg": {
        height: "32px !important"
    },
    [media.below.sm]: {
        marginBlockEnd: 32
    }
});

const animationContainer = css({
    "display": "flex",
    "justifyContent": "center",
    "alignItems": "center",
    "marginBlockEnd": 24,
    "overflow": "visible",
    "padding": "0px",
    // eslint-disable-next-line
    "> div": {
        height: 500,
        maxWidth: "none",
        overflow: "visible"
    },
    "width": "100%",
    "maxWidth": "none",
    [media.below.sm]: {
        "padding": "0px",
        "marginBlockEnd": 24,
        // eslint-disable-next-line
        "> div": {
            height: 406,
            maxWidth: "none",
            overflow: "visible"
        }
    }
});

const progressBarTextContainer = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBlockStart: 0,
    marginBlockEnd: 0,
    textAlign: "center",
    width: "100%"
});

const progressBarWrapper = css({
    width: "460px", // Slightly longer than typical text width
    marginBlockStart: 24, // Equal space from animation above
    marginBlockEnd: 20, // Space to text below
    [media.below.sm]: {
        width: "360px"
    }
});

const systemFonts = css({
    fontFamily: "var(--atlassian-recommended-system-fonts)"
});

export const JiraProductDiscoveryLoadingAnimationComponent = () => {
    const isDesktop = !useIsMobile(800);
    const [animationsMap, setAnimationsMap] = useState({
        en: []
    });

    useEffect(() => {
        const runSetAnimationMap = async () => {
            const [
                intro,
                outro,
                EN02,
                EN03,
                EN04,
                DE02,
                DE03,
                DE04,
                FR02,
                FR03,
                FR04,
                ES02,
                ES03,
                ES04,
                JA02,
                JA03,
                JA04,
                PTBR02,
                PTBR03,
                PTBR04
            ] = await Promise.all([
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/Intro.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/Outro.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/EN02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/EN03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/EN04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/DE02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/DE03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/DE04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/FR02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/FR03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/FR04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/ES02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/ES03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/ES04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/JA02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/JA03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/JA04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/PTBR02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/PTBR03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/PTBR04.json"
                ) as Promise<never>
            ]);
            const animationsMap = {
                en: [intro, EN02, EN03, EN04, outro],
                de: [intro, DE02, DE03, DE04, outro],
                fr: [intro, FR02, FR03, FR04, outro],
                es: [intro, ES02, ES03, ES04, outro],
                ja: [intro, JA02, JA03, JA04, outro],
                br: [intro, PTBR02, PTBR03, PTBR04, outro]
            };
            setAnimationsMap(animationsMap);
        };
        runSetAnimationMap();
    }, [animationsMap]);

    return (
        <>
            <div css={backdropContainer}>
                <div css={modalContainer}>
                    <div
                        css={progressContainer}
                        data-testid="signup-provisioning-module-jpd"
                    >
                        <div css={logoContainer}>
                            <JiraProductDiscoveryLogo
                                appearance="brand"
                                size="large"
                            />
                        </div>
                        <div css={animationContainer}>
                            <JiraProductDiscoveryLoadingProductComponent />
                        </div>
                        <div css={progressBarTextContainer}>
                            <div css={progressBarWrapper}>
                                <SuccessProgressBar
                                    ariaLabel="Setting up your discovery workspace"
                                    isIndeterminate
                                />
                            </div>
                            <div
                                css={[isDesktop && systemFonts]}
                                style={{
                                    fontSize: 16,

                                    color: wacToken("color.neutral.light800"),
                                    textAlign: "center"
                                }}
                            >
                                <FormattedMessage
                                    id="SignupProvisioning.inProgressText.jiraProductDiscovery.waitingForProvisioning"
                                    defaultMessage="Adding Jira Product Discovery. Less than a minute remaining..."
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};
